import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { RotateCcw } from 'lucide-react';
import { remainingSecondsAtom, sessionAtom } from '@/lib/atoms';
import { Button } from './ui/button';

/**
 * SessionTimer Component
 *
 * Displays a countdown timer showing time remaining until session restart.
 * Reads from remainingSecondsAtom which is updated by useSessionTimeout hook.
 *
 * Presentational only — timeout logic lives in useSessionTimeout (SessionLayout).
 * Rendered under the step description: plain text + restart control, not a floating chip.
 */
export function SessionTimer() {
  const remainingSeconds = useAtomValue(remainingSecondsAtom);
  const session = useAtomValue(sessionAtom);
  const navigate = useNavigate();

  const handleRestartClick = () => {
    navigate(`/${session?.id}/restart`);
  };

  if (remainingSeconds === null || session?.state === 'completed') {
    return null;
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div className="mb-3 w-full flex items-center gap-2">
      <div className="text-xs text-gray-600">
        <Trans>Time remaining: {formattedTime}</Trans>
      </div>
      <Button onClick={handleRestartClick} size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1">
        <RotateCcw className="w-3.5 h-3.5" />
        <Trans>Restart</Trans>
      </Button>
    </div>
  );
}
